import {
  type ChainFullId,
  ChainFullIds,
  type ChainId,
  type MultichainTokenBalance,
} from "@umami/chains";
import { type UnifiedBalanceWithPrices } from "@umami/state";

import { LayerZeroProvider } from "./layerZeroProvider";
import { LiFiProvider } from "./liFiProvider";
import { TezosEtherlinkProvider } from "./tezosEtherlinkProvider";
import { type EffectiveOutput, type QuoteProvider, type QuoteWithProvider } from "./types";
import { ZeroXProvider } from "./zeroXProvider";

export class QuoteManager {
  private providers: QuoteProvider[];

  constructor() {
    this.providers = [
      new ZeroXProvider(),
      new LiFiProvider(),
      new LayerZeroProvider(),
      new TezosEtherlinkProvider(),
    ];
  }

  async fetchAllQuotes(params: {
    sellToken: UnifiedBalanceWithPrices & MultichainTokenBalance;
    buyToken: UnifiedBalanceWithPrices & MultichainTokenBalance;
    sellAmount: string;
    userAddress: string;
    network: string;
    slippage: number;
    isGaslessSwap?: boolean;
    getNetworkChain: (chainId: ChainFullId) => ChainId;
  }): Promise<{ quotes: QuoteWithProvider[]; errors: string[] }> {
    const quotes: QuoteWithProvider[] = [];
    const errors: string[] = [];

    for (const provider of this.providers) {
      if (provider.canHandleSwap(params.sellToken, params.buyToken, params.getNetworkChain)) {
        try {
          const quote = await provider.fetchQuote(params);

          if (quote) {
            quotes.push(quote);
          }
        } catch (error) {
          const errorMessage = error instanceof Error ? error.message : "Unknown error";
          errors.push(`Failed to fetch ${this.getProviderName(provider)} quote: ${errorMessage}`);
        }
      }
    }

    return { quotes, errors };
  }

  selectBestQuote(
    quotes: QuoteWithProvider[],
    buyToken: UnifiedBalanceWithPrices & MultichainTokenBalance,
    getTokenUsdPrice: (symbol: string, amount: number) => number,
    getNetworkChain: (chainId: ChainFullId) => ChainId
  ): QuoteWithProvider | null {
    if (quotes.length === 0) {
      return null;
    }

    if (quotes.length === 1) {
      return quotes[0];
    }

    // Compare quotes and select the best one
    return quotes.reduce((best, current) => {
      const bestEffective = this.calculateEffectiveOutput(
        best,
        buyToken,
        getTokenUsdPrice,
        getNetworkChain
      );
      const currentEffective = this.calculateEffectiveOutput(
        current,
        buyToken,
        getTokenUsdPrice,
        getNetworkChain
      );

      // If both have USD data, compare USD values
      if (bestEffective.hasUSD && currentEffective.hasUSD) {
        return currentEffective.value > bestEffective.value ? current : best;
      }

      // If only one has USD data, prefer the one with USD data
      if (currentEffective.hasUSD && !bestEffective.hasUSD) {
        return current;
      }

      if (bestEffective.hasUSD && !currentEffective.hasUSD) {
        return best;
      }

      // If neither has USD data, compare token amounts
      // But be conservative with LayerZero since it has separate fees
      if (current.provider === "LayerZero" && best.provider !== "LayerZero") {
        return best;
      }

      if (best.provider === "LayerZero" && current.provider !== "LayerZero") {
        return current;
      }

      // If both are same type, compare token amounts
      return currentEffective.value > bestEffective.value ? current : best;
    });
  }

  private calculateEffectiveOutput(
    quote: QuoteWithProvider,
    buyToken: UnifiedBalanceWithPrices & MultichainTokenBalance,
    getTokenUsdPrice: (symbol: string, amount: number) => number,
    getNetworkChain: (chainId: ChainFullId) => ChainId
  ): EffectiveOutput {
    // Find the provider that can handle this quote
    const provider = this.providers.find(p => {
      // Create mock tokens to test if provider can handle this quote type
      const mockSellToken = { ...buyToken, chainId: ChainFullIds.ETHEREUM };
      const mockBuyToken = { ...buyToken, chainId: ChainFullIds.ETHEREUM };

      return p.canHandleSwap(mockSellToken, mockBuyToken, getNetworkChain);
    });

    if (provider) {
      return provider.getValueAndUnit(quote, buyToken, getTokenUsdPrice);
    }

    // Fallback calculation
    const buyAmountNum = parseFloat(quote.buyAmount);

    return { value: buyAmountNum, unit: buyToken.symbol, hasUSD: false };
  }

  private getProviderName(provider: QuoteProvider): string {
    if (provider instanceof ZeroXProvider) {
      return "ZeroX";
    } else if (provider instanceof LiFiProvider) {
      return "LiFi";
    } else if (provider instanceof LayerZeroProvider) {
      return "LayerZero";
    } else if (provider instanceof TezosEtherlinkProvider) {
      return "Tezos-Etherlink";
    }

    return "Unknown";
  }
}
